import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, UserPlus, Users, LogOut, Menu, X, Sparkles, ChevronLeft } from 'lucide-react';

const SalesLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const items = [
    { path: '/sales', label: 'Dashboard', icon: TrendingUp, exact: true },
    { path: '/sales/new-customer', label: 'Ny kund', icon: UserPlus },
    { path: '/sales/customers', label: 'Mina kunder', icon: Users },
  ];

  const isActive = (item) => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  return (
    <div className="min-h-screen bg-[#FAF7F2] dark:bg-zinc-950 text-foreground">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`${collapsed ? 'w-16' : 'w-60'} bg-[#1A1A1A] text-white hidden md:flex flex-col transition-all`}>
          <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between">
            {!collapsed && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#E8613A] font-bold">Operio Sales</p>
                <p className="text-xs text-white/60 mt-0.5">{user?.name || 'Säljare'}</p>
                {user?.sales_code && (
                  <p className="text-[10px] text-white/40 mt-1 font-mono">{user.sales_code}</p>
                )}
              </div>
            )}
            <button onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-white/10 rounded">
              <ChevronLeft size={14} className={collapsed ? 'rotate-180' : ''} />
            </button>
          </div>
          <nav className="flex-1 p-2 space-y-0.5">
            {items.map((it) => {
              const Icon = it.icon;
              const active = isActive(it);
              return (
                <Link
                  key={it.path}
                  to={it.path}
                  data-testid={`sales-nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    active ? 'bg-[#E8613A] text-white font-semibold' : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                  title={it.label}
                >
                  <Icon size={14} />
                  {!collapsed && <span>{it.label}</span>}
                </Link>
              );
            })}
          </nav>
          <div className="p-2 border-t border-white/10">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:bg-white/5 hover:text-white">
              <LogOut size={14} />
              {!collapsed && <span>Logga ut</span>}
            </button>
          </div>
        </aside>

        {/* Mobile menu button */}
        <div className="md:hidden fixed top-3 left-3 z-30">
          <button onClick={() => setMenuOpen(true)} className="p-2 bg-[#1A1A1A] text-white rounded-md">
            <Menu size={16} />
          </button>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#1A1A1A] text-white p-3 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-bold">{user?.name}</p>
                <button onClick={() => setMenuOpen(false)} className="p-1"><X size={14} /></button>
              </div>
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <Link key={it.path} to={it.path} onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${isActive(it) ? 'bg-[#E8613A]' : 'hover:bg-white/5'}`}>
                    <Icon size={14} />
                    <span>{it.label}</span>
                  </Link>
                );
              })}
              <div className="mt-auto pt-4 border-t border-white/10">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70">
                  <LogOut size={14} />
                  <span>Logga ut</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 p-4 md:p-8 max-w-screen-2xl mx-auto w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SalesLayout;
